import React from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Storage, ChevronRight } from "@mui/icons-material";
import ContactInfoXs from "./ContactInfoXs";

const PersonalDataNoticeXs = ({ id }) => {
  const { t } = useTranslation();

  // Helper function to render text with line breaks
  const renderWithLineBreaks = (text) => {
    return text.split("\n").map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < text.split("\n").length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Section component for consistent styling
  const Section = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle2"
        fontWeight="600"
        sx={{
          mb: 1.5,
          color: "#333",
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Box
      sx={{
        p: 2,
        maxWidth: "100%",
        overflow: "hidden",
        backgroundColor: "#f5f5f7",
      }}
      id={id}
    >
      {/* iOS-style header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          mb: 2,
          pb: 1,
          borderBottom: "1px solid rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant="subtitle1"
          component="h1"
          fontWeight="600"
          sx={{ textAlign: "center" }}
        >
          {t("privacy-policy.personal-data-notice.title")}
        </Typography>
      </Box>

      {/* Main card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Storage sx={{ color: "#007AFF", fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight="600" sx={{ ml: 1.5 }}>
              {t("privacy-policy.personal-data-notice.title")}
            </Typography>
          </Box>

          <Section
            title={t("privacy-policy.personal-data-notice.company.title")}
          >
            <Typography variant="body2" sx={{ color: "#666" }}>
              {renderWithLineBreaks(
                t("privacy-policy.personal-data-notice.company.name")
              )}
            </Typography>
          </Section>

          <Section
            title={t("privacy-policy.personal-data-notice.manager.title")}
          >
            <Typography variant="body2" sx={{ color: "#666" }}>
              {renderWithLineBreaks(
                t("privacy-policy.personal-data-notice.manager.info")
              )}
            </Typography>
          </Section>
        </CardContent>
      </Card>

      {/* Purposes card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>
              {t("privacy-policy.personal-data-notice.purposes.title")}
            </Typography>
          </Box>

          <List disablePadding>
            {t("privacy-policy.personal-data-notice.purposes.items", {
              returnObjects: true,
            }).map((item, index, array) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      variant: "body2",
                      sx: { color: "#333" },
                    }}
                    sx={{
                      m: 0,
                      "&::before": {
                        content: `"${index + 1}. "`,
                        fontWeight: 500,
                        color: "#007AFF",
                      },
                    }}
                  />
                </ListItem>
                {index < array.length - 1 && (
                  <Divider variant="inset" component="li" sx={{ ml: 2 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Complaints card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Section
            title={t("privacy-policy.personal-data-notice.complaints.title")}
          >
            <Typography variant="body2" sx={{ color: "#666" }}>
              {t("privacy-policy.personal-data-notice.complaints.text")}
            </Typography>
          </Section>
        </CardContent>
      </Card>

      {/* Disclosure card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Section
            title={t("privacy-policy.personal-data-notice.disclosure.title")}
          >
            <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
              {t("privacy-policy.personal-data-notice.disclosure.text")}
            </Typography>

            <Typography
              variant="subtitle2"
              fontWeight="500"
              sx={{ mb: 1, color: "#007AFF" }}
            >
              {t(
                "privacy-policy.personal-data-notice.disclosure.procedure.title"
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
              {t(
                "privacy-policy.personal-data-notice.disclosure.procedure.text"
              )}
            </Typography>

            <Typography
              variant="subtitle2"
              fontWeight="500"
              sx={{ mb: 1, color: "#007AFF" }}
            >
              {t("privacy-policy.personal-data-notice.disclosure.proxy.title")}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
              {t("privacy-policy.personal-data-notice.disclosure.proxy.text")}
            </Typography>

            <Box
              sx={{
                mb: 2,
                backgroundColor: "rgba(0,122,255,0.05)",
                p: 2,
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight="500" sx={{ mb: 1 }}>
                {t(
                  "privacy-policy.personal-data-notice.disclosure.proxy.legal.title"
                )}
              </Typography>
              <List disablePadding>
                {t(
                  "privacy-policy.personal-data-notice.disclosure.proxy.legal.items",
                  {
                    returnObjects: true,
                  }
                ).map((item, index) => (
                  <ListItem key={index} dense sx={{ px: 0, py: 0.75 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <ChevronRight sx={{ color: "#007AFF", fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        variant: "body2",
                        sx: { color: "#333" },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box
              sx={{
                mb: 2,
                backgroundColor: "rgba(0,122,255,0.05)",
                p: 2,
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight="500" sx={{ mb: 1 }}>
                {t(
                  "privacy-policy.personal-data-notice.disclosure.proxy.delegated.title"
                )}
              </Typography>
              <List disablePadding>
                {t(
                  "privacy-policy.personal-data-notice.disclosure.proxy.delegated.items",
                  {
                    returnObjects: true,
                  }
                ).map((item, index) => (
                  <ListItem key={index} dense sx={{ px: 0, py: 0.75 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <ChevronRight sx={{ color: "#007AFF", fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        variant: "body2",
                        sx: { color: "#333" },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Typography
              variant="subtitle2"
              fontWeight="500"
              sx={{ mb: 1, color: "#007AFF" }}
            >
              {t("privacy-policy.personal-data-notice.disclosure.fee.title")}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              {t("privacy-policy.personal-data-notice.disclosure.fee.text")}
            </Typography>
          </Section>
        </CardContent>
      </Card>

      {/* Security measures card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Section
            title={t(
              "privacy-policy.personal-data-notice.security-measures.title"
            )}
          >
            <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
              {t("privacy-policy.personal-data-notice.security-measures.intro")}
            </Typography>

            {t("privacy-policy.personal-data-notice.security-measures.items", {
              returnObjects: true,
            }).map((item, index) => (
              <React.Fragment key={index}>
                <Typography
                  variant="subtitle2"
                  fontWeight="500"
                  sx={{ mb: 1, color: "#007AFF" }}
                >
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                  {renderWithLineBreaks(item.text)}
                </Typography>
              </React.Fragment>
            ))}
          </Section>
        </CardContent>
      </Card>

      {/* Contact info card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <ContactInfoXs />
        </CardContent>
      </Card>
    </Box>
  );
};

export default PersonalDataNoticeXs;
